import React, { useCallback, useEffect, useRef, useState } from "react";
import "./search.css";
import * as FaIcons from "react-icons/fa";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { FaChevronDown, FaDownload } from "react-icons/fa";
import { FileExcelOutlined } from "@ant-design/icons";
import autoTable from "jspdf-autotable";
import { useToolbarMenuPlacement } from "./useToolbarMenuPlacement";

const HOVER_CLOSE_MS = 160;

function ExportCompactMenu({
  data,
  xlsxfilename,
  csvfilename,
  exportToCSV,
  printDocument,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);

  const { maxHeightPx, panelPositionClass } = useToolbarMenuPlacement({
    open,
    anchorRef: rootRef,
    panelRef,
    preferredMaxHeight: 200,
    itemCount: 3,
  });

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), HOVER_CLOSE_MS);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const menuItemClass =
    "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100";

  return (
    <div
      ref={rootRef}
      className="cms-export-compact-menu relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-slate-300 bg-white px-3 text-[11px] font-bold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 hover:text-blue-700"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <FaDownload size={12} />
        Export
        <FaChevronDown size={9} className="opacity-70" />
      </button>
      <div
        className={`cms-export-compact-menu__panel absolute right-0 ${panelPositionClass} z-[200] min-w-[152px] transition-opacity duration-150 ${
          open
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
        }`}
        role="menu"
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        <div
          ref={panelRef}
          className="overflow-hidden overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          style={{ maxHeight: maxHeightPx }}
        >
          <button
            type="button"
            className={menuItemClass}
            onClick={() => {
              exportToCSV(data, xlsxfilename);
              setOpen(false);
            }}
          >
            <FaIcons.FaFileExcel size={12} color="#059669" />
            Excel
          </button>
          <button
            type="button"
            className={menuItemClass}
            onClick={() => {
              printDocument(data);
              setOpen(false);
            }}
          >
            <FaIcons.FaFilePdf size={12} color="#dc2626" />
            PDF
          </button>
          <CSVLink
            data={data}
            filename={csvfilename}
            className={`${menuItemClass} no-underline`}
            target="_blank"
            onClick={() => setOpen(false)}
          >
            <FaIcons.FaFileCsv size={12} color="#007bff" />
            CSV
          </CSVLink>
        </div>
      </div>
    </div>
  );
}

const ExportOptions = ({
  pdffilename,
  xlsxfilename,
  csvfilename,
  header,
  columns,
  data,
  premium = false,
  compactMenu = false,
}) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  function printDocument(dataIntoPdf) {
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(20);
    doc.setTextColor(40);

    const columns = [
      { Header: "Index", accessor: "index" },
      { Header: "Client Name", accessor: "clientname" },
      { Header: "Course Name", accessor: "coursename" },
      { Header: "Package Name", accessor: "packagename" },
      { Header: "Type", accessor: "type" },
      { Header: "Invoice No", accessor: "invoice_No" },
      { Header: "Invoice Date", accessor: "invoice_date" },
      { Header: "Paid", accessor: "paid" },
      { Header: "Credit", accessor: "credit" },
      { Header: "Remaining", accessor: "remaining" },
      { Header: "Currency", accessor: "currency" },
      { Header: "Status", accessor: "status" },
      { Header: "Due Date", accessor: "due_date" },
      { Header: "Installments", accessor: "installments" },
      { Header: "Cost Entry Date", accessor: "cost_entry_date" },
      { Header: "Due By", accessor: "dueby" },
    ];

    // Extract headers & accessors
    const tableColumn = columns.map((c) => c.Header); // e.g. ["Name","Email"]
    const accessors = columns.map((c) => c.accessor); // e.g. ["name","email"]

    // Build table rows using accessors
    const tableRows = dataIntoPdf.map(
      (row) => accessors.map((accessor) => row[accessor] ?? "") // avoids undefined
    );
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 70,
      margin: { horizontal: 10 },
      styles: { overflow: "linebreak" },
      bodyStyles: { valign: "top" },
      theme: "striped",
      showHead: "everyPage",
      didDrawPage: function (dataArg) {
        // Header
        doc.setFontSize(18);
        doc.setTextColor("#161C22");
        doc.text(header, dataArg.settings.margin.left, 36);

        // Footer
        let str = "" + doc.internal.getNumberOfPages();
        doc.setFontSize(12);
        let pageSize = doc.internal.pageSize;
        let pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();

        doc.text(
          "Aptitude Migrations",
          dataArg.settings.margin.left,
          pageHeight - 10
        );
        doc.text(str, 575, 830);
      },
    });

    doc.save(pdffilename);
  }

  // Export CSV
  const exportToCSV = (apiData, fileName) => {
    const ws = XLSX.utils.json_to_sheet(apiData);

    // Adjust column widths
    const columnHeaders = Object.keys(apiData[0]);
    columnHeaders.forEach((header, index) => {
      const columnWidth = getColumnWidth(header, apiData);
      ws["!cols"] = ws["!cols"] || [];
      ws["!cols"][index] = { width: columnWidth };
    });

    // Bold column headers and center the text
    const headerStyle = {
      font: { bold: true },
      alignment: { horizontal: "center" },
    };
    const headerRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const columnHeaderCell = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[columnHeaderCell]) ws[columnHeaderCell] = {};
      ws[columnHeaderCell].s = headerStyle;
      ws[columnHeaderCell].v = `${columnHeaders[col]} (${col + 1})`; // Add index to the header
      ws[columnHeaderCell].t = "s"; // Treat the header value as a string
    }

    // Center align all cells
    const dataRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let row = dataRange.s.r; row <= dataRange.e.r; row++) {
      for (let col = dataRange.s.c; col <= dataRange.e.c; col++) {
        const cell = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cell]) ws[cell] = {};
        if (!ws[cell].s) ws[cell].s = {};
        ws[cell].s.alignment = { horizontal: "center" };
      }
    }

    // Center align the first column
    for (let row = dataRange.s.r; row <= dataRange.e.r; row++) {
      const cell = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (!ws[cell]) ws[cell] = {};
      if (!ws[cell].s) ws[cell].s = {};
      ws[cell].s.alignment = { horizontal: "center" };
    }

    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  };

  // Helper function to calculate column width based on the largest text
  const getColumnWidth = (header, data) => {
    const columnData = data.map((row) => String(row[header] || ""));
    const maxLength = Math.max(...columnData.map((text) => text.length));
    const width = (maxLength > 20 ? maxLength : 20) + 2; // Add some padding
    return width;
  };

  const buttonClass = premium ? "accounts-export-btn" : "btn btn-primary ml-1";
  const csvClass = premium
    ? "accounts-export-btn accounts-export-btn--csv"
    : "btn btn-primary ml-1";
  if (compactMenu) {
    return (
      <ExportCompactMenu
        data={data}
        xlsxfilename={xlsxfilename}
        csvfilename={csvfilename}
        exportToCSV={exportToCSV}
        printDocument={printDocument}
      />
    );
  }

  return (
    <>
      <button
        icon={<FileExcelOutlined />}
        className={buttonClass}
        type="primary"
        onClick={() => exportToCSV(data, xlsxfilename)}
      >
        <FaIcons.FaFileExcel
          size={12}
          color={premium ? "#059669" : "rgb(255 255 255 / 94%)"}
        />
        Excel
      </button>

      <button
        type="text"
        className={buttonClass}
        onClick={() => printDocument(data)}
      >
        <FaIcons.FaFilePdf
          size={12}
          color={premium ? "#dc2626" : "rgb(255 255 255 / 94%)"}
        />
        PDF
      </button>

      <CSVLink
        data={data}
        filename={csvfilename}
        className={csvClass}
        target="_blank"
      >
        <FaIcons.FaFileCsv
          size={12}
          color={premium ? "#007bff" : "rgb(255 255 255 / 94%)"}
        />
        CSV
      </CSVLink>
    </>
  );
};
export default ExportOptions;
